import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef, type ReactNode, type ComponentType } from 'react';
import { keyboardShortcutManager } from './utils/keyboardShortcuts';
import { useRecents } from './hooks/useRecents';
import type { Command, PageLocation } from './types';

export const DEFAULT_PLACEHOLDER = 'Search commands...';

/**
 * Core command palette context interface
 * Contains only the essential state and control functions
 */
export interface CommandPaletteContextValue {
  // Core state
  searchValue: string;
  /** The current placeholder text shown in the command input. */
  placeholder: string;
  /** Override the placeholder text. Automatically resets to the default when navigating back to global mode. */
  setPlaceholder: (value: string) => void;
  /**
   * The currently active page, or null when in global mode.
   * In global mode all registered global commands are shown.
   * When on a page, only that plugin's page commands are shown.
   */
  currentPage: PageLocation | null;
  isOpen: boolean;
  recentCommands: Command[];

  // Control functions
  setSearchValue: (value: string) => void;
  /**
   * Navigate to a specific plugin page.
   * Clears search and switches the palette to show that page's commands.
   * Optionally override the placeholder text for the duration of the page.
   */
  navigateTo: (pluginId: string, pageId: string, placeholder?: string) => void;
  /**
   * Return to global mode from any plugin page.
   */
  navigateBack: () => void;
  open: () => void;
  close: () => void;
  toggle: () => void;
  addRecent: (command: Command) => void;

  // Global command registry methods
  registerCommands: (pluginId: string, commands: Command[]) => void;
  unregisterCommands: (pluginId: string) => void;
  getAllCommands: () => Command[];
  getCommandsByGroup: () => Record<string, Command[]>;

  // Page registry methods
  /**
   * Register a React component to render when the palette navigates to this
   * plugin page. The component replaces the default command list and has full
   * control over what it renders inside the palette's `Command` element.
   */
  registerPage: (pluginId: string, pageId: string, component: ComponentType) => void;
  unregisterPage: (pluginId: string, pageId: string) => void;

  /**
   * Returns the component registered for the active page, or null when in
   * global mode. Used by the palette UI to decide what to render.
   */
  getActivePageComponent: () => ComponentType | null;

  // Shortcut registry methods
  /**
   * Register a keyboard shortcut. Provide an optional `label` to have it appear
   * in the visible shortcuts list inside the command palette footer.
   */
  registerShortcut: (shortcut: string, handler: () => void, label?: string) => () => void;

  // Loading registry methods
  /**
   * True when any plugin has registered itself as loading.
   * Drives the Footer.Loading indicator.
   */
  loading: boolean;
  /**
   * Mark a plugin as loading (true) or done (false).
   * Passing `false` removes the plugin from the loading registry.
   */
  setLoading: (pluginId: string, loading: boolean) => void;
}

const CommandPaletteContext = createContext<CommandPaletteContextValue | null>(null);

/**
 * Hook to access command palette context
 * Must be used within CommandPalette component or a plugin
 */
export function useCommandPaletteContext(): CommandPaletteContextValue {
  const context = useContext(CommandPaletteContext);

  if (!context) {
    throw new Error('useCommandPaletteContext must be used within CommandPalette');
  }

  return context;
}

/**
 * Internal provider component for the command palette context
 */
export interface CommandPaletteContextProviderProps {
  children: ReactNode;
  shortcut?: string;
  defaultPlaceholder?: string;
}

export const CommandPaletteContextProvider: React.FC<CommandPaletteContextProviderProps> = ({
  children,
  shortcut = 'ctrl+k',
  defaultPlaceholder = DEFAULT_PLACEHOLDER,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState<PageLocation | null>(null);
  const [placeholder, setPlaceholderState] = useState(defaultPlaceholder);
  const [commandRegistry, setCommandRegistry] = useState<Record<string, Command[]>>({});
  const [loadingRegistry, setLoadingRegistry] = useState<Record<string, boolean>>({});
  // pageRegistryRef: pluginId -> pageId -> component
  const pageRegistryRef = useRef<Map<string, Map<string, ComponentType>>>(new Map());

  const recents = useRecents();

  // Control functions
  const open = useCallback(() => {
    setIsOpen(true);
    setSearchValue('');
  }, []);

  const setPlaceholder = useCallback((value: string) => {
    setPlaceholderState(value);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setSearchValue('');
    setCurrentPage(null);
    setPlaceholderState(defaultPlaceholder);
  }, [defaultPlaceholder]);

  const toggle = useCallback(() => {
    if (isOpen) {
      close();
    } else {
      open();
    }
  }, [isOpen, open, close]);

  const navigateTo = useCallback((pluginId: string, pageId: string, placeholder?: string) => {
    setCurrentPage({ pluginId, pageId });
    setSearchValue('');
    if (placeholder !== undefined) {
      setPlaceholderState(placeholder);
    }
  }, []);

  const navigateBack = useCallback(() => {
    setCurrentPage(null);
    setSearchValue('');
    setPlaceholderState(defaultPlaceholder);
  }, [defaultPlaceholder]);

  // Global command registry methods
  const registerCommands = useCallback((pluginId: string, commands: Command[]) => {
    setCommandRegistry(prev => ({ ...prev, [pluginId]: commands }));
  }, []);

  const unregisterCommands = useCallback((pluginId: string) => {
    setCommandRegistry(prev => {
      const { [pluginId]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  const getAllCommands = useCallback((): Command[] => {
    return Object.values(commandRegistry).flat();
  }, [commandRegistry]);

  const getCommandsByGroup = useCallback((): Record<string, Command[]> => {
    const groups: Record<string, Command[]> = {};
    const allCommands = getAllCommands();

    for (const cmd of allCommands) {
      const category = cmd.category || 'Other';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(cmd);
    }

    return groups;
  }, [getAllCommands]);

  // Page registry methods
  const registerPage = useCallback((pluginId: string, pageId: string, component: ComponentType) => {
    if (!pageRegistryRef.current.has(pluginId)) {
      pageRegistryRef.current.set(pluginId, new Map());
    }
    pageRegistryRef.current.get(pluginId)!.set(pageId, component);
  }, []);

  const unregisterPage = useCallback((pluginId: string, pageId: string) => {
    pageRegistryRef.current.get(pluginId)?.delete(pageId);
  }, []);

  const getActivePageComponent = useCallback((): ComponentType | null => {
    if (!currentPage) return null;
    return pageRegistryRef.current.get(currentPage.pluginId)?.get(currentPage.pageId) ?? null;
  }, [currentPage]);

  // Shortcut registry methods
  const registerShortcut = useCallback((shortcut: string, handler: () => void, label?: string): (() => void) => {
    return keyboardShortcutManager.register(shortcut, handler, label);
  }, []);

  // Loading registry methods
  const setLoading = useCallback((pluginId: string, isLoading: boolean) => {
    setLoadingRegistry(prev => {
      if (!isLoading) {
        const { [pluginId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [pluginId]: true };
    });
  }, []);

  const loading = useMemo(
    () => Object.values(loadingRegistry).some(Boolean),
    [loadingRegistry],
  );

  // Keyboard shortcut registration
  useEffect(() => {
    const unregister = keyboardShortcutManager.register(shortcut, toggle);
    return unregister;
  }, [shortcut, toggle]);

  const contextValue = useMemo<CommandPaletteContextValue>(() => ({
    searchValue,
    placeholder,
    currentPage,
    isOpen,
    recentCommands: recents.recentCommands,
    setSearchValue,
    setPlaceholder,
    navigateTo,
    navigateBack,
    open,
    close,
    toggle,
    addRecent: recents.addRecent,
    registerCommands,
    unregisterCommands,
    getAllCommands,
    getCommandsByGroup,
    registerPage,
    unregisterPage,
    getActivePageComponent,
    registerShortcut,
    loading,
    setLoading,
  }), [
    searchValue,
    placeholder,
    currentPage,
    isOpen,
    recents.recentCommands,
    setPlaceholder,
    navigateTo,
    navigateBack,
    open,
    close,
    toggle,
    getActivePageComponent,
    getAllCommands,
    loading,
    setLoading,
  ]);

  return (
    <CommandPaletteContext.Provider value={contextValue}>
      {children}
    </CommandPaletteContext.Provider>
  );
};
